import { lazy, memo, useState, useRef, useEffect, useCallback} from 'react';
import { useSelector } from 'react-redux';
import { useProjectFilter } from '../../hooks/useProjectFilter.js';
import Button from '../../../bootstrap/Button.jsx';
import withSuspense from '../../../../helpers/withSuspense.jsx';
import SkeletonComponent from '../../../../skeleton_loaders/common/SkeletonComponent.jsx';
import ServerSidePaginationAggrid from '../../../common/ServerSidePaginationAggrid.jsx';
import { MARKER_COLORS, markerCellRenderer, markerFilterCellRenderer } from './MarkerCellRenderer.jsx';
import { dateFormatter } from '../../../common/AgGrid/index.jsx';
import { fetchJson } from '../../../../helpers/fetch.js';
import SkeletonGrid from '../../../../skeleton_loaders/common/SkeletonGrid.jsx';

import {
  task_list_json_path,
  task_status_options_path
} from '~/routes';

const ProjectLinkCellRenderer = withSuspense(lazy(() => import('../../../common/AgGrid/ProjectLinkCellRenderer.jsx')), SkeletonComponent);
const ProjectTaskIdCellRenderer = withSuspense(lazy(() => import('../../../task/datagrid/TaskDatagrid.jsx').then(mod => ({ default: mod.ProjectTaskIdCellRenderer }))), SkeletonComponent);

const PAGINATION_PAGE_SIZE = 10;
const FILTER_STORAGE_KEY = 'assignedTasksFilter';

const valueGetter = (params) => {
  const data = params.data || {};
  return (data[params.colDef.colId] || {})["label"];
};

const ProjectStatusCellRenderer = memo((params) => {
  const data = ((params.data || {})[params.colDef.colId] || {});
  const status = data['status'];
  const label = data['label'];
  return <span className={`label task-status ${status}`}>{label}</span>
})

export const getColumnDefs = async (opts = null, context = {}) => {
  const statusOptions = opts || await fetchJson(task_status_options_path());
  const COLUMN_DEFS = [
    {
      field: '',
      colId: 'marker',
      width: 60,
      minWidth: 53,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.keys(MARKER_COLORS),
        cellRenderer: markerFilterCellRenderer
      },
      cellRenderer: markerCellRenderer,
      valueGetter: valueGetter
    },
    {
      field: I18n.t("title"),
      colId: 'title',
      editable: false,
      flex: 1,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      cellRenderer: ProjectTaskIdCellRenderer,
      cellRendererParams: {context: {
          permissions: context.permissions,
          additionalFeatures: context.additionalFeatures,
          programmeId: context.projectId
      }},
      valueGetter: valueGetter
    },
    {
      field: I18n.t("identifier"),
      colId: 'identifier',
      editable: false,
      filter: 'agTextColumnFilter',
      cellRenderer: ProjectTaskIdCellRenderer,
      cellRendererParams: {context: {
          permissions: context.permissions,
          additionalFeatures: context.additionalFeatures,
          programmeId: context.projectId
      }},
      valueGetter: valueGetter
    },
    {
      field: I18n.t("project_identifier"),
      colId: 'project_identifier',
      editable: false,
      filter: 'agTextColumnFilter',
      cellRenderer: ProjectLinkCellRenderer,
      valueGetter: valueGetter
    },
    {
      headerName: I18n.t('changed_at'),
      field: 'created_at',
      colId: 'created_at',
      editable: false,
      filter: 'agDateColumnFilter',
      valueGetter: (params) => dateFormatter(params, 'YYYY-MM-DD HH:mm'),
      sort: "desc"
    },
    {
      field: I18n.t("status"),
      colId: 'status',
      editable: false,
      minWidth: 85,
      width: 120,
      filter: 'agSetColumnFilter',
      suppressHeaderFilterButton: true,
      filterParams: {
        values: statusOptions.map(o => String(o.id)),
        valueFormatter: (params) => {
          const item = statusOptions.find(o => o.id == params.value);;
          return item.label;
        }
      },
      cellRenderer: ProjectStatusCellRenderer,
      valueGetter: ({ data }) => data?.status?.status ?? null
    }
  ]
  return COLUMN_DEFS;
}

const AssignedTasksWidget = ({widgetId, widgetRef, theme, permissions, additionalFeatures}) => {
  const assignedTasksGridRef = useRef(null);
  const projectId = useSelector(state => state.dashboard.projectId);
  const projectFilterEnabled = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.projectFilterEnabled);
  const { activeProjectIds, isFiltered } = useProjectFilter();
  const effectiveProjectIds = projectFilterEnabled && isFiltered ? activeProjectIds.map(p => p.id) : [];
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [columnDefs, setColumnDefs] = useState(null);
  const [statusOptions, setStatusOptions] = useState(null);

  useEffect(() => {
    const setDefs = async () => {
      const opts = await fetchJson(task_status_options_path());
      const defs = await getColumnDefs(opts, { permissions, additionalFeatures, projectId });
      setStatusOptions(opts);
      setColumnDefs(defs);
    }
    setDefs();
  }, [getColumnDefs, setStatusOptions, setColumnDefs]);

  const handleGridReady = useCallback((gridApi) => {
    gridApi.sizeColumnsToFit();

    const storedFilter = localStorage.getItem(FILTER_STORAGE_KEY);
    if (storedFilter) {
      try {
        const parsed = JSON.parse(storedFilter);
        gridApi.setFilterModel(parsed);
        const statusValues = parsed.status?.values || [];
        setSelectedStatuses(statusValues);
      } catch (_) {}
    }
  }, []);


  const handleGridChange = useCallback((event) => {
    if (event.type === 'filterChanged') {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(event.filterModel));
    }
  }, []);

  const toggleStatus = useCallback((status) => {
    setSelectedStatuses(prev => {
      const newSelected = prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status];

      const gridApi = assignedTasksGridRef.current?.api;
      if (gridApi) {
        const currentModel = gridApi.getFilterModel() || {};

        if (newSelected.length) {
          currentModel.status = { values: newSelected };
        } else {
          delete currentModel.status;
        }
        gridApi.setFilterModel(currentModel);
      }
      return newSelected;
    });
  }, [assignedTasksGridRef]);

  useEffect(() => {
    let resizeTimeout;

    const onResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (assignedTasksGridRef.current?.api) {
          assignedTasksGridRef.current.api.sizeColumnsToFit();
        }
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <div className=' assigned-tasks-widget'>
      {(!columnDefs || !statusOptions) ?
        <SkeletonGrid />
      : <>
        <div className='pull-right'>
          <div className='btn-group pb-1'>
            {statusOptions.map((status,i)=>(
              <Button
                size='sm'
                type={selectedStatuses.includes(String(status.id)) ? 'primary' : 'default'}
                onClick={() => toggleStatus(String(status.id))}
                onMouseDown={(e)=>e.preventDefault()}
                key={i}
              >
                {status.label}
              </Button>
            ))}
          </div>
        </div>
        <ServerSidePaginationAggrid
          externalGridRef={assignedTasksGridRef}
          callbackOnGridChange={handleGridChange}
          onGridReadyCallback={handleGridReady}
          fetchPath={task_list_json_path({project_id: projectId})}
          fetchBody={{
            project_id: projectId,
            own_tasks_only: true,
            ...(effectiveProjectIds.length ? { project_ids: effectiveProjectIds } : {})
          }}
          refreshBodyChanges={true}
          columnDefs={columnDefs}
          defaultGridOptions={{
            pagination: true,
            paginationAutoPageSize: true,
            cacheBlockSize: PAGINATION_PAGE_SIZE,
            domLayout: 'normal'
          }}
          style={{
            height: 'calc(100% - 38px)'
          }}
        />
      </>}
    </div>
  )
}

export default AssignedTasksWidget;